import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import useQueryGetUserDetail from '@/api/auth/getUser'
import useQueryGetUserRecentState from '@/api/user/getUserRecentState'
import { useTranslation } from 'react-i18next'
import useQueryGetUserChart from '@/api/user/getUserChart'
import useQueryGetUserChart10Minutes from '@/api/user/getUserChart10Minutes'
import BiometricsChart from '@/component/chart/BiometricsChart/BiometricsChart'

export interface IProps {
    id?: number
    className?: string
    onRefresh: () => void
}

const UserDetail: React.FC<IProps> = ({ className, id = 0 }) => {
    const { t } = useTranslation()
    const { data: user } = useQueryGetUserDetail(id)
    const { data: recentState } = useQueryGetUserRecentState(id)
    const containerRef = useRef<any>(null)

    // 차트 데이터 API 호출
    const { data: dailyChartData } = useQueryGetUserChart(id)
    const { data: realtimeChartData } = useQueryGetUserChart10Minutes(id)
    
    // 데이터 변환 함수 (string x -> number x)
    const convertToNumericX = (data?: any[]) => {
        if (!data) return undefined
        return data.map(item => ({
            ...item,
            x: new Date(item.x).getTime() / 1000 // Unix timestamp로 변환
        }))
    }

    useEffect(() => {
        containerRef.current.scrollTo(0, 0)
    }, [id])

    return (
        <div className={`${className} bg-sub_b p-4 pb-20 h-full`} ref={containerRef}>
            {/* 상단 헤더 - 모바일 최적화 */}
            <div className="relative flex items-center justify-center mb-4">
                <Link 
                    to="/dashboard" 
                    className="absolute left-0 flex items-center justify-center w-8 h-8 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
                >
                    <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="text-gray-700"
                    >
                        <polyline points="15,18 9,12 15,6"></polyline>
                    </svg>
                </Link>
                <h2 className="text-base font-bold">{t('common.user')}</h2>
            </div>

            {user && (
                <>
                    {/* 사용자 정보 카드 - 모바일 최적화 */}
                    <div className="bg-white shadow-md rounded-lg p-3 w-full mb-4">
                        <p className="text-base font-bold">{user?.name}</p>
                        <p className="text-xs text-gray-600 mt-1">
                            {t('users.detail.info.address')} : {user?.address || t('common.none')}
                        </p>

                        <div className="mt-2 flex flex-col gap-1">
                            <p className="text-xs font-medium">{t('users.detail.location.device.status')}</p>
                            {user?.device && recentState?.summary ? (
                                <>
                                    {recentState.summary.hrate === null &&
                                    recentState.summary.hrateStatus === null &&
                                    recentState.summary.respiration === null &&
                                    recentState.summary.respStatus === null &&
                                    recentState.summary.isCharging === false &&
                                    !recentState.summary.isOnline &&
                                    recentState.summary.step === 0 &&
                                    (recentState.summary.battery === null || recentState.summary.battery === undefined) ? (
                                        <span className="text-xs font-bold text-gray-500">
                                            {t('users.detail.location.device.status.notUse')}
                                        </span>
                                    ) : (
                                        <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded inline-block">
                                            {!recentState.summary.isOnline
                                                ? t('users.detail.location.device.status.turnOff')
                                                : recentState.summary.isCharging && recentState.summary.isOnline
                                                    ? `⚡${t('users.detail.location.device.status.charging')} (${recentState.summary.battery}%)`
                                                    : `${recentState.summary.battery}%`}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="text-xs font-bold text-gray-500">
                                    {t('users.detail.location.device.status.none')}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* 통합 생체신호 모니터링 차트 - 모바일 최적화 */}
                    <div>
                        <BiometricsChart 
                            className={'w-full'}
                            heartRateData={convertToNumericX(dailyChartData?.hrate)}
                            respirationData={convertToNumericX(dailyChartData?.respiration)}
                            fallData={convertToNumericX(dailyChartData?.fall)}
                            realtimeHeartRate={convertToNumericX(realtimeChartData?.hrate)}
                            realtimeRespiration={convertToNumericX(realtimeChartData?.respiration)}
                            realtimeFallData={convertToNumericX(realtimeChartData?.fall)}
                            height={280}
                            title={`${user?.name} - ${t('V3.biometricDashboard.title')}`}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default UserDetail
